import { useEffect, useId, useState } from 'react';
import { loadSchoolPhases } from '@/services/schoolPhasesService';

/** Shared control for the existing Domain, choice, rule and action editors. */
export default function PhaseApplicabilityField({ schoolId, value, domainApplicability, onChange, disabled = false }) {
  const id = useId();
  const [catalogue, setCatalogue] = useState({ schoolId: null, phases: [], error: '' });
  const [reload, setReload] = useState(0);
  const mode = value?.mode ?? 'whole-school';
  useEffect(() => {
    if (mode !== 'selected-phases') return undefined;
    let current = true;
    setCatalogue({ schoolId: null, phases: [], error: '' });
    loadSchoolPhases(schoolId).then(result => {
      if (current) setCatalogue({ schoolId, phases: result.phases, error: '' });
    }).catch(error => {
      if (current) setCatalogue({ schoolId, phases: [], error: error.message || 'School phases could not be loaded.' });
    });
    return () => { current = false; };
  }, [schoolId, reload, mode]);
  const loaded = Boolean(schoolId && catalogue.schoolId === schoolId);
  const selected = Array.isArray(value?.phaseIds) ? value.phaseIds : [];
  const available = catalogue.phases.filter(phase => phase.active !== false && phase.retired !== true
    && (domainApplicability?.mode !== 'selected-phases' || domainApplicability.phaseIds?.includes(phase.id)));
  const unavailable = selected.filter(phaseId => !available.some(phase => phase.id === phaseId));
  return <fieldset className="flex flex-col gap-3" disabled={disabled}>
    <legend className="mb-2 text-sm font-medium">Applies to</legend>
    <label htmlFor={id} className="sr-only">Applies to</label>
    <select id={id} className="select select-bordered w-full" value={mode}
      onChange={event => onChange({ mode: event.target.value, phaseIds: event.target.value === 'whole-school' ? [] : selected })}>
      {!['whole-school', 'selected-phases'].includes(mode) && <option value={mode}>Review saved selection</option>}
      <option value="whole-school">Whole school</option>
      <option value="selected-phases">Selected phases</option>
    </select>
    {domainApplicability?.mode === 'selected-phases' && <p className="text-xs text-base-content/65">The Domain’s phase selection is the outer limit.</p>}
    {mode === 'selected-phases' && <div className="flex flex-col gap-2">
      {!loaded && <p role="status">Loading school phases…</p>}
      {loaded && catalogue.error && <div role="alert" className="text-sm text-error">
        {catalogue.error} <button type="button" className="btn btn-ghost btn-xs" onClick={() => setReload(number => number + 1)}>Retry</button>
      </div>}
      {loaded && !catalogue.error && !available.length && <p className="text-sm text-base-content/65">Add school phases in Setup, or choose Whole school.</p>}
      {loaded && available.map(phase => <label key={phase.id} className="flex items-center gap-2 text-sm">
        <input type="checkbox" className="checkbox checkbox-primary checkbox-sm" checked={selected.includes(phase.id)}
          onChange={event => onChange({ mode, phaseIds: event.target.checked ? [...selected, phase.id] : selected.filter(value => value !== phase.id) })} />
        {phase.name}
      </label>)}
      {loaded && unavailable.map(phaseId => <label key={phaseId} className="flex items-center gap-2 text-sm text-error">
        <input type="checkbox" className="checkbox checkbox-sm" checked
          onChange={() => onChange({ mode, phaseIds: selected.filter(value => value !== phaseId) })} />
        {catalogue.phases.find(phase => phase.id === phaseId)?.name || phaseId} — unavailable; clear this selection
      </label>)}
      {!selected.length && <p className="text-sm text-base-content/65">Choose at least one phase.</p>}
    </div>}
  </fieldset>;
}
